/*
 * The master inbox's reading area: the incumbent `.lcr-detail` on paper, in its three states before a
 * conversation is in hand (master-inbox plan, step 4). Nothing chosen: the incumbent empty heading.
 * Chosen and loading: the row already knows who and which campaign, so they head the pane at once and
 * the reply box's frame comes first, where the box lands, then the message as a placeholder in its own
 * shape, shown only after a beat (styles.css, "Opening a conversation"), so nothing moves when the
 * thread lands (newest first, LEA-514). A first load that failed says so in a sentence with Try again.
 */
import { Fragment } from "react";
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import type { InboxConversation } from "../../src/inbox-v2/contract.ts";
import { AgentMaterial } from "../agent/agent-item.tsx";
import { ApiError, failureReferenceOf } from "../http.ts";
import { BackIcon } from "../icons.tsx";
import { ThreadBelow } from "../replied-message.tsx";
import { inboxApi } from "./api.ts";
import { personName } from "./list.tsx";
import { NothingOpen } from "./nothing-open.tsx";
import { MessageBoxes, ReplyBox, type ReplyWiring } from "./reply-box.tsx";
import { useReplyBoxLink } from "./reply-box-link.ts";
import { ReplyMessageAction } from "./reply-message.tsx";
import { snoozeMenuAt } from "./snooze-menu.tsx";
import { ThreadView } from "./thread.tsx";
import { ThreadBoxesProvider, useThreadBoxes } from "./thread-boxes.tsx";
import type { InboxPageModel } from "./use-inbox-page.ts";

function attachmentHref(workspaceId: string, planId: string, id: string): string {
  const part = encodeURIComponent;
  return `/api/work/workspaces/${part(workspaceId)}/plans/${part(planId)}/attachments/${part(id)}`;
}

/** Between choosing a conversation and having it, in the loaded view's own places. */
function ThreadOpening(props: {
  row: InboxConversation | null;
  failed: boolean;
  /** The server's reference for the failure, quoted beside it; null when it gave none. */
  reference: string | null;
  onRetry: () => void;
  onBack: (() => void) | null;
}) {
  const { row } = props;
  return (
    <>
      <header className="lcr-head lcr-i2-head">
        {props.onBack && (
          <button type="button" className="lcr-back lcr-small-button" onClick={props.onBack}>
            <BackIcon size={16} /> Inbox
          </button>
        )}
        {row ? (
          <h2>
            {personName(row)}
            {row.company && <span className="lcr-i2-head-company"> · {row.company}</span>}
          </h2>
        ) : (
          <span className="lcr-skeleton lcr-skeleton-title" aria-hidden="true" />
        )}
        {row ? (
          <p className="lcr-head-meta lcr-i2-head-meta">
            <span>{row.campaign.name}</span>
          </p>
        ) : (
          <span className="lcr-skeleton lcr-skeleton-meta" aria-hidden="true" />
        )}
      </header>
      {props.failed ? (
        <p className="lcr-opening-failed" role="alert">
          Could not load this conversation
          {props.reference ? ` (reference ${props.reference})` : ""}.{" "}
          <button type="button" className="lcr-linklike" onClick={props.onRetry}>
            Try again
          </button>
        </p>
      ) : (
        <div className="lcr-thread-pane lcr-i2-thread">
          <p className="lcr-visually-hidden" role="status">
            Opening the conversation
          </p>
          <DockFrame />
          <div className="lcr-opening" aria-hidden="true">
            <div className="lcr-message lcr-received">
              <div className="lcr-opening-head">
                <span className="lcr-skeleton" data-width="name" />
                <span className="lcr-skeleton" data-width="time" />
              </div>
              <div className="lcr-opening-fields">
                {["from", "to", "subject"].map((field) => (
                  <Fragment key={field}>
                    <span className="lcr-skeleton" data-width="label" />
                    <span className="lcr-skeleton" data-width={field} />
                  </Fragment>
                ))}
              </div>
              <div className="lcr-opening-body">
                {["line-1", "line-2", "line-3", "line-4"].map((line) => (
                  <span className="lcr-skeleton" data-width={line} key={line} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The reply box's frame where it lands, first in the thread, drawn from the first frame (design
 * review "Loading", P3), so the thread under it keeps its place; the words in it wait the beat.
 */
function DockFrame() {
  return (
    <div className="lcr-bar lcr-i2-dock" aria-hidden="true">
      <div className="lcr-reply-closed">
        <span className="lcr-reply-open lcr-reply-frame">
          <span className="lcr-opening">
            <span className="lcr-skeleton" />
          </span>
        </span>
      </div>
    </div>
  );
}

/** The reply agent's part of the open conversation: on for a person whose switch is on. */
type AgentPart = {
  enabled: boolean;
  states: ReadonlyMap<string, InboxAgentState | null>;
  onUnsaved: (dirty: boolean) => void;
};

export function ReadingPane(props: { page: InboxPageModel; reply: ReplyWiring; agent: AgentPart }) {
  const { page } = props;
  // How the agent's card reaches the reply box, which owns the draft's text (step 8c).
  const link = useReplyBoxLink();
  const query = page.data.thread;
  // A refused read (401, 403) shows nothing it cached: the conversation is this person's no longer.
  const revoked = query.error instanceof ApiError && (query.error.signedOut || query.error.refused);
  const thread = revoked ? undefined : query.data;
  if (page.openId === null)
    return (
      <section className="lcr-detail lcr-i2-reading" aria-label="Conversation">
        <NothingOpen page={page} agent={props.agent.states} />
      </section>
    );
  if (!thread)
    return (
      <section
        className="lcr-detail lcr-i2-reading"
        aria-label="Conversation"
        aria-busy={!query.isError}
      >
        <ThreadOpening
          row={revoked ? null : (page.data.visible.find((row) => row.id === page.openId) ?? null)}
          failed={query.isError}
          reference={failureReferenceOf(query.error)}
          onRetry={() => void query.refetch()}
          onBack={page.phone ? page.close : null}
        />
      </section>
    );
  return (
    <OpenConversation
      page={page}
      reply={props.reply}
      agent={props.agent}
      link={link}
      thread={thread}
    />
  );
}

/** The loaded conversation, with its boxes: the reply to the lead on top, a message's above it. */
function OpenConversation(props: {
  page: InboxPageModel;
  reply: ReplyWiring;
  agent: AgentPart;
  link: ReturnType<typeof useReplyBoxLink>;
  thread: NonNullable<InboxPageModel["data"]["thread"]["data"]>;
}) {
  const { page, thread, link } = props;
  const boxes = useThreadBoxes(props.reply.userId, page.workspaceId, thread);
  const row = thread.conversation;
  const shared = {
    workspaceId: page.workspaceId,
    thread,
    focus: page.replyFocus,
    phone: page.phone,
    showKeys: page.showKeys,
    wiring: props.reply,
    onFormat: (format: "text" | "html") => {
      void inboxApi.format(page.workspaceId, row.id, format).catch(() =>
        page.setToast({
          text: "Reply format could not be saved. Sending uses the format shown.",
          undo: null,
        }),
      );
    },
  };
  return (
    <ThreadBoxesProvider value={boxes}>
      <section className="lcr-detail lcr-i2-reading" aria-label="Conversation">
        {/* The agent's material is a timeline item under the reply box (step 8c, D8; LEA-514). */}
        {/* The thread is just below it: the quote of the newest message folds to one line. */}
        <ThreadBelow.Provider
          value={{
            name: row.leadName,
            address: row.leadAddress,
            newestId: thread.messages.at(-1)?.id ?? null,
          }}
        >
          <AgentMaterial
            workspaceId={page.workspaceId}
            planId={row.id}
            enabled={props.agent.enabled}
            state={props.agent.states.get(row.id) ?? null}
            viewerId={props.reply.userId}
            link={link}
            onUnsaved={props.agent.onUnsaved}
          >
            {(agent) => (
              <ThreadView
                workspaceId={page.workspaceId}
                thread={thread}
                showKeys={page.showKeys}
                selecting={page.selecting}
                relabelOpen={page.relabelOpen}
                onBack={page.phone ? page.close : null}
                onRelabelOpen={page.setRelabelOpen}
                moreOpen={page.moreOpen}
                onMoreOpen={page.setMoreOpen}
                onRelabel={(label) => void page.relabel(label, [row])}
                onConfirm={() => void page.confirm([row])}
                onMeeting={(meeting) => page.meeting(row, meeting)}
                onDone={() => void page.done([row])}
                onTrash={() => void page.trash([row])}
                inTrash={page.box === "trash"}
                onSeen={(value) => page.seen([row], value)}
                onSnoozeOpen={() => page.snoozeMenu([row], "thread")}
                snoozeMenu={snoozeMenuAt(page, "thread")}
                assign={
                  page.assigning.assignment && {
                    assignment: page.assigning.assignment,
                    open: page.assigning.open,
                    onOpenChange: page.assigning.setOpen,
                    onChoose: (value) =>
                      void page.assigning.assign(
                        value,
                        [{ ...row, snooze: thread.handoverSnooze ?? null }],
                        thread.assignment?.revision,
                      ),
                  }
                }
                onCampaign={(id) => {
                  const { search } = page.list;
                  const rest = search.tokens.filter((token) => token.kind !== "campaign-id");
                  search.setTokens([...rest, { kind: "campaign-id", value: id }]);
                }}
                attachmentHref={(id) => attachmentHref(page.workspaceId, row.id, id)}
                replyAction={(message) => (
                  <ReplyMessageAction
                    workspaceId={page.workspaceId}
                    planId={row.id}
                    message={message}
                    mailbox={thread.mailbox}
                  />
                )}
                afterNewest={agent?.item}
                reply={
                  <ReplyBox
                    key={`${page.workspaceId}:${row.id}`}
                    {...shared}
                    draft={agent?.draft ?? null}
                    draftWaits={agent?.waits ?? null}
                    link={agent ? link : null}
                  />
                }
                boxAbove={(message) => <MessageBoxes {...shared} message={message} />}
              />
            )}
          </AgentMaterial>
        </ThreadBelow.Provider>
      </section>
    </ThreadBoxesProvider>
  );
}
